import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Checkbox,
  FormControlLabel,
  MenuItem,
  CircularProgress
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { productService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

// Estructura de datos para menús dependientes y campos dinámicos
const PRODUCT_STRUCTURE = {
  mercados: {
    industrial: {
      label: 'Metal Industrial',
      metales: {
        aluminio: {
          label: 'Aluminio',
          presentaciones: {
            bobina: {
              label: 'Bobina',
              campos: ['largo', 'ancho', 'espesor', 'norma', 'aleacion', 'acabado']
            },
            lamina: {
              label: 'Lámina',
              campos: ['largo', 'ancho', 'espesor', 'norma', 'aleacion', 'acabado']
            },
            placa: {
              label: 'Placa',
              campos: ['largo', 'ancho', 'espesor', 'norma', 'aleacion', 'acabado']
            },
            perfil: {
              label: 'Perfil Extrudido',
              campos: ['tipo_perfil', 'dimensiones', 'largo', 'aleacion', 'acabado']
            },
            lingote: {
              label: 'Lingote',
              campos: ['peso', 'dimensiones', 'aleacion']
            },
            barra: {
              label: 'Barra',
              campos: ['forma', 'dimensiones', 'largo', 'aleacion', 'acabado']
            },
            tubo: {
              label: 'Tubo',
              campos: ['diametro_exterior', 'espesor_pared', 'largo', 'aleacion', 'acabado']
            }
          }
        },
        cobre: {
          label: 'Cobre',
          presentaciones: {
            catodo: {
              label: 'Cátodo',
              campos: ['peso', 'dimensiones', 'pureza']
            },
            alambron: {
              label: 'Alambrón',
              campos: ['diametro', 'largo', 'pureza']
            },
            alambre: {
              label: 'Alambre',
              campos: ['diametro', 'largo', 'pureza']
            },
            tuberia: {
              label: 'Tubería',
              campos: ['diametro_exterior', 'espesor_pared', 'largo', 'pureza']
            },
            lamina: {
              label: 'Lámina',
              campos: ['largo', 'ancho', 'espesor', 'pureza']
            },
            barra: {
              label: 'Barra',
              campos: ['forma', 'dimensiones', 'largo', 'pureza']
            }
          }
        }
        // ...otros metales industriales
      }
    },
    reciclaje: {
      label: 'Chatarra / Reciclaje',
      metales: {
        aluminio: {
          label: 'Aluminio',
          presentaciones: {
            paca_botes: {
              label: 'Paca de Botes',
              campos: ['peso_aprox', 'dimensiones_paca', 'calidad', 'origen']
            },
            perfil: {
              label: 'Perfil',
              campos: ['tipo_perfil', 'dimensiones', 'largo', 'calidad', 'origen']
            },
            mixto: {
              label: 'Chatarra Mixta',
              campos: ['calidad', 'origen']
            },
            viruta: {
              label: 'Viruta',
              campos: ['calidad', 'origen']
            },
            rin: {
              label: 'Rin',
              campos: ['calidad', 'origen']
            }
          }
        },
        cobre: {
          label: 'Cobre',
          presentaciones: {
            cable_brillo: {
              label: 'Cable Brillo',
              campos: ['calidad', 'origen']
            },
            tuberia: {
              label: 'Tubería',
              campos: ['calidad', 'origen']
            },
            rebaba: {
              label: 'Rebaba',
              campos: ['calidad', 'origen']
            }
          }
        }
        // ...otros metales reciclaje
      }
    }
  }
};

const PublishProduct = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Estados para los menús dependientes
  const [mercado, setMercado] = useState('');
  const [metal, setMetal] = useState('');
  const [presentacion, setPresentacion] = useState('');

  // Obtener opciones dinámicas
  const mercadosOptions = Object.entries(PRODUCT_STRUCTURE.mercados).map(([key, val]) => ({ value: key, label: val.label }));
  const metalesOptions = mercado ? Object.entries(PRODUCT_STRUCTURE.mercados[mercado].metales).map(([key, val]) => ({ value: key, label: val.label })) : [];
  const presentacionesOptions = (mercado && metal) ? Object.entries(PRODUCT_STRUCTURE.mercados[mercado].metales[metal].presentaciones).map(([key, val]) => ({ value: key, label: val.label })) : [];

  // Limpiar selección dependiente si cambia el padre
  const handleMercadoChange = (e) => {
    setMercado(e.target.value);
    setMetal('');
    setPresentacion('');
  };
  const handleMetalChange = (e) => {
    setMetal(e.target.value);
    setPresentacion('');
  };
  const handlePresentacionChange = (e) => {
    setPresentacion(e.target.value);
  };

  const [formData, setFormData] = useState({
    description: '',
    price: '',
    contact_for_price: false,
    moq: '',
    image: null
  });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    // Aquí podrías cargar categorías desde la API si tienes endpoint
    setCategories([
      { value: 'steel-coil', label: 'Acero - Bobina' },
      { value: 'steel-sheet', label: 'Acero - Lámina' },
      { value: 'aluminum-coil', label: 'Aluminio - Bobina' },
      { value: 'aluminum-sheet', label: 'Aluminio - Lámina' },
      { value: 'copper-bar', label: 'Cobre - Barra' },
      { value: 'copper-pipe', label: 'Cobre - Tubo' },
    ]);
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === 'checkbox') {
      setFormData({ ...formData, [name]: checked });
    } else if (type === 'file') {
      setFormData({ ...formData, image: files[0] });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const data = new FormData();
      // Campos generales
      data.append('description', formData.description);
      if (formData.price) data.append('price', formData.price);
      data.append('contact_for_price', formData.contact_for_price);
      if (formData.moq) data.append('moq', formData.moq);
      if (formData.image) data.append('image', formData.image);
      
      // Título generado
      if (nombreProductoGenerado) {
        data.append('title', nombreProductoGenerado);
      }
      
      // Guardar selección de mercado, metal y presentación
      data.append('mercado', mercado);
      data.append('metal', metal);
      data.append('presentacion', presentacion);
      
      // Especificaciones dinámicas y campos principales
      const fieldMapping = {
        dimensiones: 'dimensions',
        espesor: 'thickness',
        peso: 'weight',
        norma: 'standard',
      };

      const especificaciones = {};
      getCamposDinamicos().forEach((campo) => {
        if (formData[campo]) {
          const modelFieldName = fieldMapping[campo];
          if (modelFieldName) {
            data.append(modelFieldName, formData[campo]);
          } else {
            especificaciones[campo] = formData[campo];
          }
        }
      });
      data.append('especificaciones', JSON.stringify(especificaciones));
      
      // Lógica para enviar el producto
      await productService.createProduct(data);
      setSuccess('¡Producto publicado exitosamente!');
      setTimeout(() => navigate('/products'), 1500);
    } catch (err) {
      setError('Error al publicar el producto.');
    } finally {
      setLoading(false);
    }
  };

  // Función para obtener los campos dinámicos según la selección
  const getCamposDinamicos = () => {
    if (!mercado || !metal || !presentacion) return [];
    return PRODUCT_STRUCTURE.mercados[mercado].metales[metal].presentaciones[presentacion].campos;
  };

  // Función para generar el nombre del producto automáticamente
  const generarNombreProducto = () => {
    if (!mercado || !metal || !presentacion) return '';
    const mercadoLabel = PRODUCT_STRUCTURE.mercados[mercado].label;
    const metalLabel = PRODUCT_STRUCTURE.mercados[mercado].metales[metal].label;
    const presentacionLabel = PRODUCT_STRUCTURE.mercados[mercado].metales[metal].presentaciones[presentacion].label;
    // Puedes agregar algún campo clave de especificaciones si lo deseas
    let extra = '';
    if (formData.espesor) extra = ` - ${formData.espesor}mm`;
    if (formData.diametro) extra = ` - Ø${formData.diametro}mm`;
    if (formData.tipo_perfil) extra = ` - ${formData.tipo_perfil}`;
    return `${mercadoLabel} - ${metalLabel} - ${presentacionLabel}${extra}`;
  };

  // Etiquetas amigables para los campos
  const LABELS = {
    largo: 'Largo (mm)',
    ancho: 'Ancho (mm)',
    espesor: 'Espesor (mm)',
    norma: 'Norma (ASTM, ISO, etc.)',
    aleacion: 'Aleación',
    acabado: 'Acabado',
    tipo_perfil: 'Tipo de Perfil',
    dimensiones: 'Dimensiones',
    forma: 'Forma',
    diametro: 'Diámetro (mm)',
    diametro_exterior: 'Diámetro Exterior (mm)',
    espesor_pared: 'Espesor de Pared (mm)',
    peso: 'Peso (kg)',
    peso_aprox: 'Peso Aproximado por Paca (kg)',
    dimensiones_paca: 'Dimensiones de la Paca (cm)',
    calidad: 'Calidad o Tipo',
    origen: 'Origen',
    pureza: 'Pureza',
  };

  // Opciones para campos tipo select
  const ACABADOS = ['Laminado en Caliente', 'Laminado en Frío', 'Pulido', 'Galvanizado'];
  const ORIGENES = ['Post-Industrial', 'Demolición', 'Post-Consumo'];

  // Llama a las funciones una sola vez por render
  const camposDinamicos = getCamposDinamicos();
  const nombreProductoGenerado = generarNombreProducto();

  if (!user || user.role !== 'supplier') {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Alert severity="error">Solo los proveedores pueden publicar productos.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Publicar producto
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Completa la información para publicar tu producto en MetalConnect.
        </Typography>
        {/* Vista previa del nombre generado */}
        {nombreProductoGenerado && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" color="primary">
              Nombre generado: {nombreProductoGenerado}
            </Typography>
          </Box>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        {/* Menús dependientes */}
        <Box sx={{ mb: 2 }}>
          <TextField
            select
            fullWidth
            label="Mercado"
            value={mercado}
            onChange={handleMercadoChange}
            sx={{ mb: 2 }}
          >
            <MenuItem value="" disabled>Selecciona un mercado</MenuItem>
            {mercadosOptions.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            fullWidth
            label="Metal"
            value={metal}
            onChange={handleMetalChange}
            sx={{ mb: 2 }}
            disabled={!mercado}
          >
            <MenuItem value="" disabled>Selecciona un metal</MenuItem>
            {metalesOptions.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            fullWidth
            label="Presentación"
            value={presentacion}
            onChange={handlePresentacionChange}
            sx={{ mb: 2 }}
            disabled={!metal}
          >
            <MenuItem value="" disabled>Selecciona una presentación</MenuItem>
            {presentacionesOptions.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </TextField>
        </Box>
        {/* Campos dinámicos según la presentación */}
        {camposDinamicos.length > 0 && (
          <Box sx={{ mb: 2 }}>
            {camposDinamicos.map((campo) => {
              if (campo === 'acabado') {
                return (
                  <TextField
                    key={campo}
                    select
                    fullWidth
                    label={LABELS[campo]}
                    name={campo}
                    value={formData[campo] || ''}
                    onChange={handleChange}
                    sx={{ mb: 2 }}
                    required
                  >
                    <MenuItem value="" disabled>Selecciona un acabado</MenuItem>
                    {ACABADOS.map(opt => (
                      <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                    ))}
                  </TextField>
                );
              }
              if (campo === 'origen') {
                return (
                  <TextField
                    key={campo}
                    select
                    fullWidth
                    label={LABELS[campo]}
                    name={campo}
                    value={formData[campo] || ''}
                    onChange={handleChange}
                    sx={{ mb: 2 }}
                    required
                  >
                    <MenuItem value="" disabled>Selecciona un origen</MenuItem>
                    {ORIGENES.map(opt => (
                      <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                    ))}
                  </TextField>
                );
              }
              return (
                <TextField
                  key={campo}
                  fullWidth
                  label={LABELS[campo] || campo}
                  name={campo}
                  value={formData[campo] || ''}
                  onChange={handleChange}
                  sx={{ mb: 2 }}
                  required
                />
              );
            })}
          </Box>
        )}
        <Box component="form" onSubmit={handleSubmit} encType="multipart/form-data">
          <TextField
            margin="normal"
            required
            fullWidth
            multiline
            minRows={2}
            id="description"
            label="Descripción"
            name="description"
            value={formData.description}
            onChange={handleChange}
            disabled={loading}
          />
          <TextField
            margin="normal"
            fullWidth
            id="price"
            label="Precio (MXN)"
            name="price"
            type="number"
            value={formData.price}
            onChange={handleChange}
            disabled={formData.contact_for_price || loading}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.contact_for_price}
                onChange={handleChange}
                name="contact_for_price"
                color="primary"
                disabled={loading}
              />
            }
            label="Contactar para cotización (no mostrar precio)"
          />
          <TextField
            margin="normal"
            fullWidth
            id="moq"
            label="Cantidad mínima de pedido (MOQ)"
            name="moq"
            type="number"
            value={formData.moq}
            onChange={handleChange}
            disabled={loading}
          />
          <Box sx={{ mt: 2, mb: 2 }}>
            <Button
              variant="contained"
              component="label"
              sx={{ backgroundColor: '#1e3a8a', '&:hover': { backgroundColor: '#1e40af' } }}
              disabled={loading}
            >
              Subir imagen
              <input
                type="file"
                name="image"
                accept="image/*"
                hidden
                onChange={handleChange}
              />
            </Button>
            {formData.image && (
              <Typography variant="body2" sx={{ ml: 2, display: 'inline' }}>
                {formData.image.name}
              </Typography>
            )}
          </Box>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 2, py: 1.5, backgroundColor: '#f59e0b', '&:hover': { backgroundColor: '#d97706' } }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Publicar producto'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default PublishProduct; 